import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { GetInterventionLadderResponse, InterventionLadderMonthModel, InterventionLadderStepModel } from '../../../api-service/clients';
import { CircularProgressCentered } from '../../../components/circular-progress-centered';
import { formatMonth, formatNumber, toDate } from '../../../utilities';
import { arrivalColor, arrivalHoverColor, departureColor, departureHoverColor, sinceStartColor } from '../intervention-ladder/intervention-ladder-colors';
import { changesSinceStart, formatChange, InterventionArrivalsChart, sinceStartLabel } from './intervention-arrivals-chart';

const visuallyHidden = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  margin: '-1px',
  padding: 0,
  border: 0,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const;

export interface InterventionArrivalsComponentProps {
  ladder: GetInterventionLadderResponse | undefined;
  selectedStep: number;
}

export function InterventionArrivalsComponent({ ladder, selectedStep }: InterventionArrivalsComponentProps) {
  const step = ladder?.steps.find(candidate => candidate.step === selectedStep);
  if (ladder !== undefined && step === undefined) {
    return null;
  }

  return (
    <Paper sx={{ p: 2, mt: 2, display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h2" sx={{ fontSize: '1rem', mb: 0.5 }}>
        Tilgang og afgang pr. måned
      </Typography>
      {ladder === undefined || step === undefined ? <CircularProgressCentered /> : <InterventionArrivals step={step} from={toDate(ladder.from)!} to={toDate(ladder.to)!} />}
    </Paper>
  );
}

interface InterventionArrivalsProps {
  step: InterventionLadderStepModel;
  from: Date;
  to: Date;
}

function InterventionArrivals({ step, from, to }: InterventionArrivalsProps) {
  const months = step.months.map(month => ({ ...month, isPartial: isPartial(month, from, to) }));
  const sinceStart = changesSinceStart(
    months.map(month => month.arrivals),
    months.map(month => month.departures)
  );

  return (
    <>
      <Typography sx={{ fontSize: '0.8125rem', fontStyle: 'italic', color: 'text.secondary' }}>
        Antal borgere ind og ud af{' '}
        <Box component="strong" sx={{ fontStyle: 'normal', fontWeight: 600, color: 'text.primary' }}>
          {step.title}
        </Box>{' '}
        · {formatDay(from)} – {formatDay(to)}
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, mt: 1, mb: 1, fontSize: '0.75rem', color: 'text.secondary' }}>
        <LegendItem color={arrivalColor} label="Tilgang" value={formatNumber(step.arrivals)} />
        <LegendItem color={departureColor} label="Afgang" value={formatNumber(step.departures)} />
        <LegendItem color={sinceStartColor} label={sinceStartLabel} value={formatChange(step.arrivals - step.departures)} isLine />
      </Box>
      <InterventionArrivalsChart
        labels={months.map((month, index) => {
          const label = formatMonth(month.month).substring(0, 3);
          return index === 0 || month.month === 1 ? [label, `${month.year}`] : [label];
        })}
        monthTitles={months.map(monthTitle)}
        sinceStart={sinceStart}
        arrivals={{
          label: 'Tilgang',
          counts: months.map(month => month.arrivals),
          barColors: months.map(month => barColor(arrivalColor, month.isPartial)),
          // Chart.js can't make color-mix(), which the partial months use, a shade darker itself.
          hoverColors: months.map(month => barColor(arrivalHoverColor, month.isPartial)),
        }}
        departures={{
          label: 'Afgang',
          counts: months.map(month => month.departures),
          barColors: months.map(month => barColor(departureColor, month.isPartial)),
          hoverColors: months.map(month => barColor(departureHoverColor, month.isPartial)),
        }}
      />
      <Box component="table" sx={visuallyHidden}>
        <caption>Tilgang og afgang pr. måned på {step.title}</caption>
        <thead>
          <tr>
            <th scope="col">Måned</th>
            <th scope="col">Tilgang</th>
            <th scope="col">Afgang</th>
            <th scope="col">{sinceStartLabel}</th>
          </tr>
        </thead>
        <tbody>
          {months.map((month, index) => (
            <tr key={`${month.year}-${month.month}`}>
              <th scope="row">{monthTitle(month)}</th>
              <td>{month.arrivals}</td>
              <td>{month.departures}</td>
              <td>{formatChange(sinceStart[index])}</td>
            </tr>
          ))}
        </tbody>
      </Box>
    </>
  );
}

interface LegendItemProps {
  color: string;
  label: string;
  value: string;
  isLine?: boolean;
}

function LegendItem({ color, label, value, isLine }: LegendItemProps) {
  return (
    <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      {isLine ? (
        <Box component="span" sx={{ width: 16, borderTop: `2px solid ${color}` }} />
      ) : (
        <Box component="span" sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: color }} />
      )}
      {label}
      <Box component="strong" sx={{ color: 'text.primary', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </Box>
    </Box>
  );
}

function isPartial({ year, month }: InterventionLadderMonthModel, from: Date, to: Date) {
  return new Date(year, month - 1, 1) < from || new Date(year, month, 0) > to;
}

function monthTitle({ year, month, isPartial }: { year: number; month: number; isPartial: boolean }) {
  return `${formatMonth(month)} ${year}${isPartial ? ' (ikke hel måned)' : ''}`;
}

function barColor(color: string, isPartialMonth: boolean) {
  return isPartialMonth ? `color-mix(in oklch, ${color} 40%, #ffffff)` : color;
}

function monthAbbreviation(month: number) {
  return formatMonth(month).substring(0, 3).toLowerCase();
}

function formatDay(date: Date) {
  return `${date.getDate()}. ${monthAbbreviation(date.getMonth() + 1)} ${date.getFullYear()}`;
}
